import React from 'react';
import type { FormikProps } from 'formik';
import type { OptionType } from '@withjoy/joykit';
import { Box, FormControl, InputV2, NativeSelect, TextV2 } from '@withjoy/joykit';
import { useId } from '@withjoy/joykit/hooks';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { useLocalizedCountryOptions } from '@shared/utils/address/useLocalizedCountryOptions';
import type { Contacts, FieldVisibility, IframeContacts } from '@apps/guest/contactCollector/shared/types';
import { RegistrationFieldLabel } from './RegistrationFieldLabel';
import { FieldPair } from './RegistrationContactFields';

type RegistrationMailingAddressProps = Readonly<{
  formik: FormikProps<Contacts> | FormikProps<IframeContacts>;
  mailingAddressRule: Exclude<FieldVisibility, 'hidden'>;
  onCountryChange: (option: OptionType | null, index: number) => void;
  errors: Readonly<{ address1?: string; city?: string; region?: string; postalCode?: string; country?: string }>;
}>;

/**
 * The mailing address on the registration contact step: a light-bordered card attached under the
 * guest's own details, so the contact fields stay primary.
 *
 * It is always open when the host asks for an address. A collapsed section is not in the page, so
 * browser autofill could not fill it. Every field keeps its autocomplete token, and Country is a
 * native select so autofill can set it too.
 */
export const RegistrationMailingAddress: React.FC<RegistrationMailingAddressProps> = ({ formik, mailingAddressRule, onCountryChange, errors }) => {
  const { tq } = useTranslation('eventFormDisplay');
  const { countryOptions } = useLocalizedCountryOptions();
  const fieldId = useId(undefined, 'registration-address');
  const headingId = `${fieldId}-heading`;
  const contact = formik.values.contacts[0];

  const isRequired = mailingAddressRule === 'required';
  const field = (name: 'address1' | 'address2' | 'city' | 'region' | 'postalCode', label: React.ReactElement, autoComplete: string, error?: string) => (
    <FormControl
      // Required for assistive tech; the label carries no mark (D2). The apartment line never is.
      isRequired={isRequired && name !== 'address2'}
      label={label}
      isInvalid={!!error}
      error={error}
    >
      <InputV2
        id={`${fieldId}-${name}`}
        {...formik.getFieldProps(`contacts[0].${name}`)}
        autoComplete={autoComplete}
      />
    </FormControl>
  );

  return (
    <Box
      role="group"
      aria-labelledby={headingId}
      display="flex"
      flexDirection="column"
      gap={6}
      // 32px under the contact fields: more than the 24px between fields, so the card reads as attached but separate.
      marginTop={7}
      padding={6}
      border="1px solid"
      borderColor="inputBorder"
      borderRadius={pxToRem(16)}
    >
      <TextV2
        id={headingId}
        typographyVariant="hed2"
        fontWeight={600}
        // 20px to the first label: 24px of gap, less 4px.
        marginBottom={-1}
      >
        <RegistrationFieldLabel
          text={tq`Mailing address`}
          isOptional={mailingAddressRule === 'optional'}
        />
      </TextV2>
      {field('address1', <RegistrationFieldLabel text={tq`Street address`} />, 'address-line1', errors.address1)}
      {field(
        'address2',
        <RegistrationFieldLabel
          text={tq`Apartment, suite, etc.`}
          isOptional
        />,
        'address-line2',
      )}
      <FieldPair>
        {field('city', <RegistrationFieldLabel text={tq`City`} />, 'address-level2', errors.city)}
        {field('region', <RegistrationFieldLabel text={tq`State, province or territory`} />, 'address-level1', errors.region)}
      </FieldPair>
      <FieldPair>
        {field('postalCode', <RegistrationFieldLabel text={tq`ZIP or postal code`} />, 'postal-code', errors.postalCode)}
        <FormControl
          label={<RegistrationFieldLabel text={tq`Country or region`} />}
          isInvalid={!!errors.country}
          error={errors.country}
        >
          <NativeSelect
            id={`${fieldId}-country`}
            options={countryOptions}
            value={contact?.countryCode}
            autoComplete="country"
            onChange={countryCode => onCountryChange(countryOptions.find(option => option.value === countryCode) ?? null, 0)}
          />
        </FormControl>
      </FieldPair>
    </Box>
  );
};
